import type maplibregl from 'maplibre-gl';
import { MAP_THEMES, applyTheme, saveThemeId } from '../../map/mapTheme.js';
import { createDisclosure } from './disclosure.js';

const THEME_SVG =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
  'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
  '<circle cx="12" cy="12" r="9"/><path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor"/></svg>';

export interface ThemeSwitcherDeps {
  map: maplibregl.Map;
  /** Current theme id at build time (restored from storage). */
  currentThemeId: string;
  /** Runs after a theme is applied and its style has loaded (re-apply radar, retint). */
  onThemeApplied: (themeId: string) => void;
}

/** Build the Theme button + a popover of swatches; persists + applies on select. */
export function createThemeSwitcher(parent: HTMLElement, deps: ThemeSwitcherDeps): { destroy(): void } {
  const { map, onThemeApplied } = deps;
  let activeId = deps.currentThemeId;

  const disclosure = createDisclosure(parent, {
    ariaLabel: 'Map theme',
    svg: THEME_SVG,
    panelClass: 'panel panel--theme',
    haspopup: 'listbox',
  });
  const { panel, setOpen } = disclosure;

  const swatches = new Map<string, HTMLButtonElement>();
  for (const theme of MAP_THEMES) {
    const swatch = document.createElement('button');
    swatch.type = 'button';
    swatch.className = `theme-swatch theme-swatch--${theme.id}`;
    swatch.setAttribute('aria-pressed', String(theme.id === activeId));
    const dot = document.createElement('span');
    dot.className = 'theme-swatch-dot';
    const label = document.createElement('span');
    label.className = 'theme-swatch-label';
    label.textContent = theme.label;
    swatch.append(dot, label);
    swatch.addEventListener('click', () => {
      if (theme.id === activeId) {
        setOpen(false);
        return;
      }
      activeId = theme.id;
      for (const [id, el] of swatches) el.setAttribute('aria-pressed', String(id === activeId));
      saveThemeId(theme.id);
      applyTheme(map, theme.id, () => onThemeApplied(theme.id));
      setOpen(false);
    });
    swatches.set(theme.id, swatch);
    panel.appendChild(swatch);
  }
  return { destroy: disclosure.destroy };
}
